import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  FundProjectionScreenOutlined,
  PlayCircleOutlined,
  PlusCircleFilled,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Button, message, Popconfirm, Table } from 'antd';
import { Book, BookAppendix, BookAppendixBaseItem, BookAppendixExerciseFormat, Exam } from 'model/response';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import {
  deleteBookAppendix,
  findAllBookAppendixByBookId,
  pushBookAppendixDown,
  pushBookAppendixUp,
} from '../../../../service/bookAppendixService';
import processError from '../../../../utils/processError';
import { bookAppendixItemTypeList, getEnumLabel } from '../../../../utils/enumMapper';
import {
  deleteBookAppendixItem,
  pushBookAppendixItemDown,
  pushBookAppendixItemUp,
} from '../../../../service/bookAppendixItemService';
import BookAppendixFormDrawer from '../BookAppendixFormDrawer';
import BookAppendixItemFormDrawer from '../BookAppendixItemFormDrawer';
import { getBookDetail } from 'service/bookService';
import LectureTheoryModal from '../../../common/LectureTheoryModal';
import ExamPreviewModal from '../../ExamManagement/ExamPreviewModal';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../redux/store/store';
import { setLoading } from '../../../../redux/slice/appConfigSlice';
import { useAppConfigIsLoading } from '../../../../redux/selector';

interface TableRow {
  type: 'BOOK_APPENDIX' | 'BOOK_APPENDIX_ITEM';
  bookAppendix: BookAppendix | null;
  item: BookAppendixBaseItem | null;
  parent?: BookAppendix;
  level: number;
}

type PreviewModalDataType = {
  open: boolean;
  exam?: Exam;
};

export default function BookAppendixTable() {
  const { bookId } = useParams();
  const [book, setBook] = useState<Book | null>(null);
  const [bookAppendices, setBookAppendices] = useState<BookAppendix[]>([]);
  const [data, setData] = useState<TableRow[]>([]);
  const [bookAppendixFromDrawerData, setBookAppendixFromDrawerData] = useState<{
    open: boolean;
    mode: 'create' | 'update';
    parent?: BookAppendix;
    book?: Book | null;
    defaultBookAppendix?: BookAppendix;
  }>({
    open: false,
    mode: 'create',
  });
  const [bookAppendixItemFormDrawerData, setBookAppendixItemFormDrawerData] = useState<{
    open: boolean;
    bookAppendix?: BookAppendix;
    book?: Book;
  }>({
    open: false,
  });
  const [lectureTheoryModalData, setLectureTheoryModalData] = useState<{
    type: 'LECTURE' | 'THEORY';
    id: string | null;
    open: boolean;
  }>({
    type: 'LECTURE',
    id: null,
    open: false,
  });
  const [preview, setPreview] = useState<PreviewModalDataType>({
    open: false,
  });
  const isLoading = useAppConfigIsLoading();
  const dispatch = useDispatch<AppDispatch>();

  function processRowData(rowData: TableRow[], bookAppendix: BookAppendix, level: number, parent?: BookAppendix) {
    rowData.push({
      level: level,
      type: 'BOOK_APPENDIX',
      bookAppendix: bookAppendix,
      item: null,
      parent,
    });
    [
      ...bookAppendix.bookAppendixTheories,
      ...bookAppendix.bookAppendixLectures,
      ...bookAppendix.bookAppendixExams,
      ...bookAppendix.bookAppendixExerciseFormats,
      ...bookAppendix.bookAppendixPractices,
    ]
      .sort((item1, item2) => item1.itemIndex - item2.itemIndex)
      .forEach((item) => {
        rowData.push({
          level: level + 1,
          type: 'BOOK_APPENDIX_ITEM',
          bookAppendix: null,
          item,
          parent: bookAppendix,
        });
      });
    [...bookAppendix.children]
      .sort((b1, b2) => b1.bookAppendixIndex - b2.bookAppendixIndex)
      .forEach((child) => processRowData(rowData, child, level + 1, bookAppendix));
  }

  async function fetchBookDetail() {
    if (!bookId) return;
    dispatch(setLoading(true));
    try {
      setBook(await getBookDetail(bookId));
      const rowData: TableRow[] = [];
      const bookAppendices = await findAllBookAppendixByBookId(bookId);
      setBookAppendices(bookAppendices);
      [...bookAppendices]
        .sort((b1, b2) => b1.bookAppendixIndex - b2.bookAppendixIndex)
        .forEach((bookAppendix) => {
          processRowData(rowData, bookAppendix, 1);
        });
      setData(rowData);
    } catch (error) {
      processError(error);
    } finally {
      dispatch(setLoading(false));
    }
  }

  useEffect(() => {
    if (bookId) {
      fetchBookDetail().then().catch(processError);
    }
  }, [bookId]);

  const getRowType = useCallback((row: TableRow) => {
    if (row.type === 'BOOK_APPENDIX') {
      return `Phụ lục cấp ${row.level}`;
    }
    if (
      row.item &&
      row.item.type === 'EXERCISE_FORMAT' &&
      (row.item as BookAppendixExerciseFormat).exerciseFormat.child
    ) {
      return 'Dạng bài tập (Ẩn trong mức độ thông thạo)';
    }
    return getEnumLabel(row.item?.type, bookAppendixItemTypeList);
  }, []);

  const getItemIcon = useCallback((row: TableRow) => {
    if (!row.item || !row.item.type) {
      return null;
    }
    if (row.item.type === 'LECTURE') {
      return <PlayCircleOutlined className={'text-0B7BFF'} />;
    }
    if (row.item.type === 'THEORY') {
      return <FilePdfOutlined className={'text-0B7BFF'} />;
    }
    if (row.item.type === 'EXERCISE_FORMAT') {
      if ((row.item as BookAppendixExerciseFormat).exerciseFormat.child) {
        return <FolderOpenOutlined className={'text-38c172'} />;
      } else {
        return <FolderOpenOutlined className={'text-0B7BFF'} />;
      }
    }
    if (row.item.type === 'EXAM') {
      return <FileTextOutlined className={'text-0B7BFF'} />;
    }
    return <FundProjectionScreenOutlined className={'text-0B7BFF'} />;
  }, []);

  const canPushUp = useCallback((row: TableRow) => {
    if (row.type === 'BOOK_APPENDIX' && row.bookAppendix) {
      return row.bookAppendix.bookAppendixIndex > 0;
    }
    if (row.type === 'BOOK_APPENDIX_ITEM' && row.item) {
      return row.item.itemIndex > 0;
    }
    return false;
  }, []);

  const canPushDown = useCallback(
    (row: TableRow) => {
      if (!book) return false;
      let parentChildrenSize = bookAppendices.length;
      if (row.parent) {
        parentChildrenSize =
          row.type === 'BOOK_APPENDIX'
            ? row.parent.children.length
            : row.parent.bookAppendixExams.length +
              row.parent.bookAppendixLectures.length +
              row.parent.bookAppendixTheories.length +
              row.parent.bookAppendixExerciseFormats.length +
              row.parent.bookAppendixPractices.length;
      }
      if (row.type === 'BOOK_APPENDIX' && row.bookAppendix) {
        return row.bookAppendix.bookAppendixIndex < parentChildrenSize - 1;
      }
      if (row.type === 'BOOK_APPENDIX_ITEM' && row.item) {
        return row.item.itemIndex < parentChildrenSize - 1;
      }
      return false;
    },
    [book, bookAppendices],
  );

  async function handlePushUp(row: TableRow) {
    try {
      if (row.type === 'BOOK_APPENDIX') {
        if (!row.bookAppendix) {
          return;
        }
        await pushBookAppendixUp(row.bookAppendix.id);
        await fetchBookDetail();
        message.info('Đẩy mục lục lên trên thành công');
      } else {
        if (!row.item) {
          return;
        }
        await pushBookAppendixItemUp(row.item.id);
        await fetchBookDetail();
        message.info('Đẩy mục lên trên thành công');
      }
    } catch (err) {
      processError(err);
    }
  }

  async function handlePushDown(row: TableRow) {
    try {
      if (row.type === 'BOOK_APPENDIX') {
        if (!row.bookAppendix) {
          return;
        }
        await pushBookAppendixDown(row.bookAppendix.id);
        await fetchBookDetail();
        message.info('Đẩy mục lục xuống dưới thành công');
      } else {
        if (!row.item) {
          return;
        }
        await pushBookAppendixItemDown(row.item.id);
        await fetchBookDetail();
        message.info('Đẩy mục xuống dưới thành công');
      }
    } catch (err) {
      processError(err);
    }
  }

  function handleCloseBookAppendixForm() {
    setBookAppendixFromDrawerData({
      open: false,
      mode: 'create',
    });
  }

  async function handleCloseBookAppendixFormAndReloadPage() {
    try {
      await fetchBookDetail();
      handleCloseBookAppendixForm();
    } catch (err) {
      processError(err);
    }
  }

  function handleCloseBookAppendixItemForm() {
    setBookAppendixItemFormDrawerData({
      open: false,
    });
  }

  async function handleCloseBookAppendixItemFormAndReloadPage() {
    try {
      await fetchBookDetail();
      handleCloseBookAppendixItemForm();
    } catch (err) {
      processError(err);
    }
  }

  async function handleAddRootBookAppendix() {
    setBookAppendixFromDrawerData({
      open: true,
      mode: 'create',
      book: book,
    });
  }

  async function handleAddChildBookAppendix(row: TableRow) {
    if (!row.bookAppendix) return;
    setBookAppendixFromDrawerData({
      open: true,
      mode: 'create',
      book: book,
      parent: row.bookAppendix,
    });
  }

  async function handleUpdateBookAppendix(row: TableRow) {
    if (!row.bookAppendix) return;
    setBookAppendixFromDrawerData({
      open: true,
      mode: 'update',
      book: book,
      defaultBookAppendix: row.bookAppendix,
    });
  }

  async function handleAddBookAppendixItem(row: TableRow) {
    setBookAppendixItemFormDrawerData({
      open: true,
      book: book || undefined,
      bookAppendix: row.bookAppendix || undefined,
    });
  }

  async function handleDelete(row: TableRow) {
    try {
      if (row.type === 'BOOK_APPENDIX') {
        if (!row.bookAppendix) {
          return;
        }
        await deleteBookAppendix(row.bookAppendix.id);
        await fetchBookDetail();
        message.info('Xóa phụ lục thành công');
      } else {
        if (!row.item) {
          return;
        }
        await deleteBookAppendixItem(row.item.id);
        await fetchBookDetail();
        message.info('Xóa mục thành công');
      }
    } catch (err) {
      processError(err);
    }
  }

  function handleItemClick(row: TableRow) {
    if (row.type !== 'BOOK_APPENDIX_ITEM') {
      return;
    }
    if (row.item?.type === 'LECTURE' || row.item?.type === 'THEORY') {
      setLectureTheoryModalData({
        type: row.item.type,
        open: true,
        id: row.item.itemId,
      });
      return;
    }
    if (row.item?.type === 'EXAM') {
      setPreview({
        open: true,
        exam: (row.item as any).exam,
      });
      return;
    }
    if (row.item?.type === 'PRACTICE') {
      setPreview({
        open: true,
        exam: (row.item as any).practice,
      });
      return;
    }
  }

  const isItemVisible = (bookAppendixBaseItem: BookAppendixBaseItem | any) => {
    if (!bookAppendixBaseItem) return false;
    if (bookAppendixBaseItem.type === 'LECTURE') {
      return bookAppendixBaseItem.lecture.webViewActive;
    }
    if (bookAppendixBaseItem.type === 'EXERCISE_FORMAT') {
      return bookAppendixBaseItem.exerciseFormat.webViewActive;
    }
    if (bookAppendixBaseItem.type === 'THEORY') {
      return bookAppendixBaseItem.theory.webViewActive;
    }
    if (bookAppendixBaseItem.type === 'EXAM') {
      return bookAppendixBaseItem.exam.approvalStatus === 'APPROVED';
    }
    if (bookAppendixBaseItem.type === 'PRACTICE') {
      return bookAppendixBaseItem.practice.approvalStatus === 'APPROVED';
    }
  };

  const getItemHiddenMessage = (bookAppendixBaseItem: BookAppendixBaseItem | any) => {
    if (bookAppendixBaseItem.type === 'EXAM' || bookAppendixBaseItem.type === 'PRACTICE') {
      return 'Đang kiểm duyệt / đã từ chối';
    }
    return 'Đã ẩn';
  };

  const columns: ColumnsType<TableRow> = [
    {
      title: 'Tên chương',
      dataIndex: 'name',
      align: 'left',
      width: '50%',
      render: (_, row) => (
        <div style={{ marginLeft: `${row.level * 8}px` }}>
          {row.type === 'BOOK_APPENDIX' ? (
            <span className={'font-bold'} style={{ fontSize: `${18 - row.level}px` }}>
              {row.bookAppendix?.name}
            </span>
          ) : (
            <div className={'flex items-center gap-x-2 cursor-pointer'} onClick={() => handleItemClick(row)}>
              <span>{getItemIcon(row)}</span>
              <span style={{ fontSize: `${18 - row.level}px` }} className={'text-0B7BFF'}>
                {(row.item?.itemIndex || 0) + 1}. {row.item?.displayTitle}
              </span>
              {!isItemVisible(row.item) ? (
                <span className={'font-bold inline-block min-w-[52px]'}>({getItemHiddenMessage(row.item)})</span>
              ) : null}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Loại',
      dataIndex: 'rowType',
      align: 'left',
      width: '15%',
      render: (_, row) => <span>{getRowType(row)}</span>,
    },
    {
      title: 'Level',
      dataIndex: 'level',
      align: 'left',
      width: '15%',
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      align: 'center',
      width: '20%',
      render: (_, row) => (
        <div className="flex item-center w-full justify-center gap-2">
          {canPushDown(row) ? (
            <button className="p-2 rounded bg-[#cdf1ff]" onClick={() => handlePushDown(row)}>
              <DownOutlined />
            </button>
          ) : null}
          {canPushUp(row) ? (
            <button className="p-2 rounded bg-[#cdf1ff]" onClick={() => handlePushUp(row)}>
              <UpOutlined />
            </button>
          ) : null}
          {row.type === 'BOOK_APPENDIX' ? (
            <>
              <button className="p-2 rounded bg-[#FFFFFF] text-red" onClick={() => handleAddBookAppendixItem(row)}>
                <PlusOutlined />
              </button>
              <button className="p-2 rounded bg-[#33CC66] text-white" onClick={() => handleAddChildBookAppendix(row)}>
                <PlusCircleFilled />
              </button>
              <button className="p-2 rounded bg-[#0066FF] text-white" onClick={() => handleUpdateBookAppendix(row)}>
                <EditOutlined />
              </button>
            </>
          ) : null}
          <Popconfirm
            title={'Xóa ' + getRowType(row)}
            description={'Xác nhận xóa ' + getRowType(row)}
            onConfirm={() => handleDelete(row)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-[#FF0000] text-white">
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded h-[calc(100vh-112px)] overflow-y-auto">
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex flex-col gap-4">
          <Button
            onClick={handleAddRootBookAppendix}
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới phụ lục cấp 1</span>
          </Button>
        </div>
      </div>
      <div>
        <div className="flex flex-col ">
          <Table
            columns={columns}
            dataSource={data}
            rowKey={(record) => record.bookAppendix?.id || record.item?.id || ''}
            loading={isLoading}
            pagination={false}
            bordered
          />
        </div>
      </div>
      <BookAppendixFormDrawer
        open={bookAppendixFromDrawerData.open}
        mode={bookAppendixFromDrawerData.mode}
        parent={bookAppendixFromDrawerData.parent}
        book={bookAppendixFromDrawerData.book || undefined}
        defaultBookAppendix={bookAppendixFromDrawerData.defaultBookAppendix}
        onBeforeSuccess={handleCloseBookAppendixFormAndReloadPage}
        onClose={handleCloseBookAppendixForm}
      />
      <BookAppendixItemFormDrawer
        {...bookAppendixItemFormDrawerData}
        onBeforeSuccess={handleCloseBookAppendixItemFormAndReloadPage}
        onClose={handleCloseBookAppendixItemForm}
      />
      <LectureTheoryModal
        open={lectureTheoryModalData.open}
        id={lectureTheoryModalData.id}
        type={lectureTheoryModalData.type}
        onCancel={() =>
          setLectureTheoryModalData({
            type: 'LECTURE',
            id: null,
            open: false,
          })
        }
      />
      <ExamPreviewModal
        open={preview.open}
        exam={preview.exam}
        onCancel={() =>
          setPreview({
            open: false,
          })
        }
        zIndex={1000}
      />
    </div>
  );
}
